// Aviation Jobs Platform - TypeScript Types

export type JobWizardType = 
  | 'job_post'       // Company posting a job
  | 'job_contract'   // Company seeking contractor/freelancer
  | 'job_profile'    // Professional creating their profile (FREE)
  | 'job_seeking';   // Professional actively seeking position (FREE)

export type JobCategory = 
  | 'pilot' 
  | 'mechanic' 
  | 'camo' 
  | 'part21' 
  | 'cabin_crew' 
  | 'ground_ops' 
  | 'atc_dispatch' 
  | 'training' 
  | 'drones_uas' 
  | 'airport_ops' 
  | 'engineering' 
  | 'management';

// Shared job category options for consistency across wizard and marketplace
export const JOB_CATEGORY_OPTIONS: { value: JobCategory; label: string; shortLabel: string }[] = [
  { value: 'pilot', label: 'Pilots', shortLabel: 'Pilots' },
  { value: 'mechanic', label: 'Mechanics & Maintenance', shortLabel: 'Mechanics' },
  { value: 'camo', label: 'CAMO / Continuing Airworthiness', shortLabel: 'CAMO' },
  { value: 'part21', label: 'Part-21 (Design & Production)', shortLabel: 'Part-21' },
  { value: 'cabin_crew', label: 'Cabin Crew', shortLabel: 'Cabin Crew' },
  { value: 'ground_ops', label: 'Ground Operations', shortLabel: 'Ground Ops' },
  { value: 'atc_dispatch', label: 'ATC & Dispatch', shortLabel: 'ATC & Dispatch' },
  { value: 'training', label: 'Training & Education', shortLabel: 'Training' },
  { value: 'drones_uas', label: 'Drones & UAS', shortLabel: 'Drones/UAS' },
  { value: 'airport_ops', label: 'Airport Operations', shortLabel: 'Airport Ops' },
  { value: 'engineering', label: 'Engineering & Technical', shortLabel: 'Engineering' },
  { value: 'management', label: 'Management & Administration', shortLabel: 'Management' },
];

export type EmploymentType = 
  | 'full_time' 
  | 'part_time' 
  | 'contract' 
  | 'freelance' 
  | 'internship' 
  | 'seasonal';

export type SalaryType = 'hourly' | 'daily' | 'monthly' | 'annual' | 'negotiable' | 'competitive';

export type RemoteOptions = 'onsite' | 'remote' | 'hybrid';

export type MedicalClass = 'Class 1' | 'Class 2' | 'Class 3';

export type CompanySize = 'startup' | 'small' | 'medium' | 'enterprise';

// Contract/Freelancer specific types
export type ProjectDuration = '1_week' | '2_weeks' | '1_month' | '3_months' | '6_months' | '12_months' | 'ongoing';

export type UrgencyLevel = 'asap' | 'within_1_week' | 'within_1_month' | 'flexible';

export const PROJECT_DURATION_LABELS: Record<ProjectDuration, string> = {
  '1_week': '1 Week',
  '2_weeks': '2 Weeks',
  '1_month': '1 Month',
  '3_months': '3 Months',
  '6_months': '6 Months',
  '12_months': '12 Months',
  'ongoing': 'Ongoing / Open-ended',
};

export const URGENCY_LEVEL_LABELS: Record<UrgencyLevel, string> = {
  'asap': 'ASAP / Immediately',
  'within_1_week': 'Within 1 Week',
  'within_1_month': 'Within 1 Month',
  'flexible': 'Flexible / Negotiable',
};

export interface JobCategoryInfo {
  id: string;
  slug: JobCategory;
  name: string;
  description?: string;
  icon_name: string;
  display_order: number;
  is_active: boolean;
}

export interface JobListing {
  id: string;
  user_id: string;
  listing_type: JobWizardType;
  job_category: JobCategory;
  employment_type?: EmploymentType;
  title: string;
  description?: string;
  requirements?: string;
  benefits?: string;
  responsibilities?: string;
  
  // Company Info
  company_name?: string;
  company_logo?: string;
  company_description?: string;
  company_website?: string;
  company_size?: CompanySize;
  
  // Compensation
  salary_min?: number;
  salary_max?: number;
  salary_type?: SalaryType;
  currency?: string;
  includes_benefits?: boolean;
  
  // Location
  city?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  remote_options?: RemoteOptions;
  relocation_offered?: boolean;
  
  // Requirements/Qualifications
  experience_years_min?: number;
  experience_years_max?: number;
  licenses?: string[];
  certifications?: string[];
  type_ratings?: string[];
  medical_class?: MedicalClass;
  flight_hours_min?: number;
  security_clearance?: string;
  
  // Professional Profile Fields
  resume_url?: string;
  portfolio_url?: string;
  linkedin_url?: string;
  current_position?: string;
  available_from?: string;
  notice_period?: string;
  willing_to_relocate?: boolean;
  preferred_locations?: string[];
  
  // Contract/Freelancer specific fields
  contract_start_date?: string;
  contract_end_date?: string;
  project_duration?: string;
  urgency_level?: string;
  
  // Contact
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  
  // Media
  hero_image?: string;
  photos?: string[];
  
  // Status
  status?: string;
  featured?: boolean;
  expires_at?: string;
  visibility_settings?: Record<string, boolean>;
  
  created_at?: string;
  updated_at?: string;
}

// Pilot experience breakdown
export interface PilotExperience {
  totalFlightHours?: number | null;
  picHours?: number | null;
  sicHours?: number | null;
  nightHours?: number | null;
  ifrHours?: number | null;
  crossCountryHours?: number | null;
  turbineHours?: number | null;
  multiEngineHours?: number | null;
  helicopterPicHours?: number | null;
  instructorHours?: number | null;
  last90DaysHours?: number | null;
}

// Mechanic experience breakdown
export interface MechanicExperience {
  totalExperienceYears?: number | null;
  turbineEngineExperience?: boolean;
  pistonEngineExperience?: boolean;
  helicopterExperience?: boolean;
  fixedWingExperience?: boolean;
  heavyCheckExperience?: boolean;
  lineMaintenance?: boolean;
  shopMaintenance?: boolean;
  avionicsExperience?: boolean;
  compositeRepair?: boolean;
  ndtCertified?: boolean;
  majorRepairsCount?: number | null;
  majorAlterationsCount?: number | null;
  primarySpecialty?: string;
  secondarySpecialty?: string;
}

export type WorkAuthorization = 
  | 'citizen'
  | 'permanent_resident'
  | 'work_visa'
  | 'student_visa'
  | 'sponsorship_required'
  | 'any';

export type SecurityClearance = 
  | 'none'
  | 'public_trust'
  | 'airport_badge'
  | 'secret'
  | 'top_secret'
  | 'top_secret_sci';

// Job Document Categories
export type JobDocumentCategory = 
  | 'resume'
  | 'cover_letter'
  | 'pilot_license'
  | 'medical_certificate'
  | 'mechanic_license'
  | 'type_rating_cert'
  | 'training_records'
  | 'logbook_summary'
  | 'education'
  | 'references'
  | 'portfolio'
  | 'background_check'
  | 'other';

// Job Document Upload Interface
export interface JobDocumentUpload {
  id: string;
  category: JobDocumentCategory;
  name: string;
  file?: File;
  url?: string;
  size?: number;
  mimeType?: string;
  isConfidential?: boolean;
  expiresAt?: string;
  verificationStatus?: 'pending' | 'verified' | 'expired';
}

// Education Entry Interface
export interface EducationEntry {
  id: string;
  institution: string;
  degree: string;
  fieldOfStudy?: string;
  startDate?: string;
  endDate?: string;
  isCurrent?: boolean;
  description?: string;
}

// Reference Entry Interface
export interface ReferenceEntry {
  id: string;
  name: string;
  title?: string;
  company?: string;
  relationship: string;
  email?: string;
  phone?: string;
  yearsKnown?: number;
  isConfidential?: boolean;
}

// Portfolio Item Interface
export interface PortfolioItem {
  id: string;
  title: string;
  description?: string;
  url?: string;
  imageUrl?: string;
  type: 'project' | 'achievement' | 'publication' | 'other';
}

export type ShiftPreference = 
  | 'day_shift'
  | 'night_shift'
  | 'rotating'
  | 'on_call'
  | 'flexible'
  | 'travel_required'
  | 'weekends';

export interface JobWizardFormData {
  // Core
  jobCategory?: JobCategory;
  employmentType?: EmploymentType;
  title?: string;
  description?: string;
  requirements?: string;
  benefits?: string;
  responsibilities?: string;
  
  // Company Info (for job posts)
  companyName?: string;
  companyLogo?: string;
  companyDescription?: string;
  companyWebsite?: string;
  companySize?: CompanySize;
  useCompanyProfile?: boolean;
  
  // Compensation
  salaryMin?: number | null;
  salaryMax?: number | null;
  salaryType?: SalaryType;
  currency?: string;
  includesBenefits?: boolean;
  selectedBenefits?: string[];
  
  // Location
  city?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  remoteOptions?: RemoteOptions;
  relocationOffered?: boolean;
  
  // Requirements
  experienceYearsMin?: number | null;
  experienceYearsMax?: number | null;
  licenses?: string[];
  certifications?: string[];
  skills?: string[];
  typeRatings?: TypeRating[];
  medicalClass?: MedicalClass;
  flightHoursMin?: number | null;
  securityClearance?: SecurityClearance;
  
  // NEW: Professional experience cards
  pilotExperience?: PilotExperience;
  mechanicExperience?: MechanicExperience;
  
  // NEW: Work authorization & scheduling
  workAuthorization?: WorkAuthorization;
  shiftPreferences?: ShiftPreference[];
  
  // Professional Profile
  resumeUrl?: string;
  portfolioUrl?: string;
  linkedinUrl?: string;
  currentPosition?: string;
  availableFrom?: string;
  noticePeriod?: string;
  willingToRelocate?: boolean;
  preferredLocations?: string[];
  
  // General experience summary (for non-pilot/mechanic categories)
  experienceSummary?: string;
  keyAchievements?: string;
  
  // Contact & Privacy
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  visibilitySettings?: Record<string, boolean>;
  
  // NEW: Profile headline/tagline
  headline?: string;
  
  // NEW: Documents
  documents?: JobDocumentUpload[];
  
  // NEW: Education
  education?: EducationEntry[];
  
  // NEW: References
  references?: ReferenceEntry[];
  
  // NEW: Portfolio
  portfolioItems?: PortfolioItem[];
  
  // Work history preference (CV vs manual entry)
  useResumeForHistory?: boolean;
  
  // Media
  heroImage?: string | null;
  photos?: string[];
  
  // Contract/Freelancer specific fields
  contractStartDate?: string;
  contractEndDate?: string;
  projectDuration?: ProjectDuration;
  urgencyLevel?: UrgencyLevel;
  
  // Status
  featured?: boolean;
}

// Type rating used in wizard and selector (matches hook's TypeRating)
export interface TypeRating {
  rating_type: 'helicopter' | 'engine';
  manufacturer: string;
  model: string;
  reference_id?: string;
  hours_on_type?: number;
  is_required?: boolean;
}

// Legacy alias for compatibility
export type TypeRatingEntry = TypeRating;

// Job listing type display info
export const JOB_LISTING_TYPE_INFO: Record<JobWizardType, { label: string; description: string; forCompanies: boolean }> = {
  job_post: {
    label: 'Post a Job',
    description: 'Companies posting permanent positions',
    forCompanies: true,
  },
  job_contract: {
    label: 'Hire Contractor',
    description: 'Companies seeking freelancers or contractors',
    forCompanies: true,
  },
  job_profile: {
    label: 'Create Profile',
    description: 'Professionals showcasing their experience',
    forCompanies: false,
  },
  job_seeking: {
    label: 'Seeking Position',
    description: 'Professionals actively looking for work',
    forCompanies: false,
  },
};

// Common aviation licenses
export const AVIATION_LICENSES = [
  'CPL (Commercial Pilot License)',
  'ATPL (Airline Transport Pilot License)',
  'PPL (Private Pilot License)',
  'MPL (Multi-Crew Pilot License)',
  'A&P (Airframe & Powerplant)',
  'AME (Aircraft Maintenance Engineer)',
  'IA (Inspection Authorization)',
  'CFI (Certified Flight Instructor)',
  'CFII (Certified Flight Instructor Instrument)',
  'MEI (Multi-Engine Instructor)',
  'ATP (Airline Transport Pilot)',
  'FE (Flight Engineer)',
  'Dispatcher License',
  'Ground Instructor',
];

// Common aviation certifications
export const AVIATION_CERTIFICATIONS = [
  'EASA Part-66',
  'EASA Part-145',
  'EASA Part-147',
  'EASA Part-21',
  'FAA Part-65',
  'FAA Part-121',
  'FAA Part-135',
  'FAA Part-145',
  'ICAO Annex 1',
  'SMS (Safety Management Systems)',
  'CRM (Crew Resource Management)',
  'Dangerous Goods',
  'NDT (Non-Destructive Testing)',
  'Avionics Rating',
  'Glass Cockpit Certified',
];
